import React, { useState, useEffect } from "react";
import { QuestionBank, Trait, Section, QuestionTypeMaster } from "../../types";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { Input, Select, Textarea } from "../ui/FormFields";
import { toast } from "react-hot-toast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  editingQuestion?: any;
  banks: QuestionBank[];
  selectedBankId?: string;
  traits: Trait[];
  sections: Section[];
  questionTypes: QuestionTypeMaster[];
}

interface OptionField {
  id: string;
  text: string;
  isCorrect?: boolean;
  value?: string;
  order?: number;
}

interface MatrixRow {
  id: string;
  text: string;
}

interface MatrixColumn {
  id: string;
  text: string;
}

interface LikertItem {
  id: string;
  text: string;
}

const QuestionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  editingQuestion,
  banks,
  selectedBankId,
  traits,
  sections,
  questionTypes
}) => {
  const [selectedBank, setSelectedBank] = useState("");
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [selectedTrait, setSelectedTrait] = useState("");
  const [marks, setMarks] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState<QuestionTypeMaster[]>([]);
  const [showTraitSelector, setShowTraitSelector] = useState(false);

  // Selection Inputs
  const [options, setOptions] = useState<OptionField[]>([]);
  const [newOptionText, setNewOptionText] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState<string | string[]>("");
  const [dropdownOptions, setDropdownOptions] = useState<string[]>([]);
  const [newDropdownOption, setNewDropdownOption] = useState("");

  // Text Inputs
  const [textDefaultValue, setTextDefaultValue] = useState("");
  const [textareaDefaultValue, setTextareaDefaultValue] = useState("");
  const [richTextContent, setRichTextContent] = useState("");

  // Number Inputs
  const [numberMin, setNumberMin] = useState<number>(0);
  const [numberMax, setNumberMax] = useState<number>(100);
  const [numberDefault, setNumberDefault] = useState<number>(0);
  const [decimalMin, setDecimalMin] = useState<number>(0);
  const [decimalMax, setDecimalMax] = useState<number>(100);
  const [decimalDefault, setDecimalDefault] = useState<number>(0);
  const [decimalPrecision, setDecimalPrecision] = useState<number>(2);
  const [currencySymbol, setCurrencySymbol] = useState<string>("$");
  const [currencyDefault, setCurrencyDefault] = useState<number>(0);

  // Date & Time
  const [dateFormat, setDateFormat] = useState<string>("YYYY-MM-DD");
  const [timeFormat, setTimeFormat] = useState<string>("HH:mm");
  const [datetimeFormat, setDatetimeFormat] = useState<string>("YYYY-MM-DD HH:mm");

  // Rating & Scale
  const [starRatingMax, setStarRatingMax] = useState<number>(5);
  const [starRatingDefault, setStarRatingDefault] = useState<number>(0);
  const [numericScaleMin, setNumericScaleMin] = useState<number>(1);
  const [numericScaleMax, setNumericScaleMax] = useState<number>(10);
  const [numericScaleDefault, setNumericScaleDefault] = useState<number>(5);
  const [likertScaleItems, setLikertScaleItems] = useState<LikertItem[]>([]);
  const [newLikertItem, setNewLikertItem] = useState("");
  const [likertScaleOptions, setLikertScaleOptions] = useState<string[]>([
    "Strongly Disagree",
    "Disagree",
    "Neutral",
    "Agree",
    "Strongly Agree"
  ]);
  const [sliderMin, setSliderMin] = useState<number>(0);
  const [sliderMax, setSliderMax] = useState<number>(100);
  const [sliderDefault, setSliderDefault] = useState<number>(50);
  const [sliderStep, setSliderStep] = useState<number>(1);
  const [trueFalseAnswer, setTrueFalseAnswer] = useState<boolean | null>(null);
  // File & Media
  const [fileAllowedTypes, setFileAllowedTypes] = useState<string>("");
  const [fileMaxSize, setFileMaxSize] = useState<number>(5);
  const [imageMaxSize, setImageMaxSize] = useState<number>(2);
  const [imageDimensions, setImageDimensions] = useState<string>("");

  // Advanced Inputs
  const [colorDefault, setColorDefault] = useState<string>("#000000");

  // Structured Inputs
  const [emailDefault, setEmailDefault] = useState("");
  const [phoneDefault, setPhoneDefault] = useState("");
  const [urlDefault, setUrlDefault] = useState("");
  const [passwordMinLength, setPasswordMinLength] = useState<number>(6);

  // Drag & Drop UI
  const [dragReorderItems, setDragReorderItems] = useState<{ id: string; text: string; order: number }[]>([]);
  const [dragMatchItems, setDragMatchItems] = useState<{ id: string; left: string; right: string }[]>([]);
  const [dragCategorizeItems, setDragCategorizeItems] = useState<{ id: string; text: string; category: string }[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState("");

  // Matrix Input
  const [matrixRows, setMatrixRows] = useState<MatrixRow[]>([]);
  const [matrixColumns, setMatrixColumns] = useState<MatrixColumn[]>([]);
  const [newRowText, setNewRowText] = useState("");
  const [newColumnText, setNewColumnText] = useState("");
  const [matrixCellType, setMatrixCellType] = useState<"radio" | "checkbox">("radio");

  // Likert Scale State
  const [likertScaleRows, setLikertScaleRows] = useState<LikertItem[]>([]);
  const [newLikertRow, setNewLikertRow] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (editingQuestion) {
        // Load editing question data
        setSelectedBank(editingQuestion.bankId);
        setQuestionText(editingQuestion.text || editingQuestion.questionText || "");
        setQuestionType(editingQuestion.type || editingQuestion.questionType || "");
        setSelectedTrait(editingQuestion.traitId || "");
        setMarks(editingQuestion.marks || 1);

        // Load type-specific data
        loadTypeSpecificData(editingQuestion);

        const bank = banks.find(b => b.id === editingQuestion.bankId);
        if (bank) {
          const section = sections.find(s => s.id === bank.sectionId);
          setSelectedSection(section || null);
        }
      } else if (selectedBankId) {
        setSelectedBank(selectedBankId);
        const bank = banks.find(b => b.id === selectedBankId);
        if (bank) {
          const section = sections.find(s => s.id === bank.sectionId);
          setSelectedSection(section || null);
        }
      } else if (banks.length > 0) {
        setSelectedBank(banks[0].id);
        const section = sections.find(s => s.id === banks[0].sectionId);
        setSelectedSection(section || null);
      }
    }
  }, [isOpen, editingQuestion, selectedBankId, banks, sections]);

  const loadTypeSpecificData = (question: any) => {
    // Selection Inputs
    setOptions(question.options || []);
    setCorrectAnswer(question.correctAnswer || "");
    setDropdownOptions(question.dropdownOptions || []);

    // Text Inputs
    setTextDefaultValue(question.defaultValue || "");
    setTextareaDefaultValue(question.defaultValue || "");
    setRichTextContent(question.defaultValue || "");

    // Number Inputs
    setNumberMin(question.min || 0);
    setNumberMax(question.max || 100);
    setNumberDefault(question.defaultValue || 0);
    setDecimalMin(question.min || 0);
    setDecimalMax(question.max || 100);
    setDecimalDefault(question.defaultValue || 0);
    setDecimalPrecision(question.precision || 2);
    setCurrencySymbol(question.currencySymbol || "$");
    setCurrencyDefault(question.defaultValue || 0);

    // Rating & Scale
    setStarRatingMax(question.max || 5);
    setStarRatingDefault(question.defaultValue || 0);
    setNumericScaleMin(question.min || 1);
    setNumericScaleMax(question.max || 10);
    setNumericScaleDefault(question.defaultValue || 5);
    setLikertScaleItems(question.likertItems || []);
    setSliderMin(question.min || 0);
    setSliderMax(question.max || 100);
    setSliderDefault(question.defaultValue || 50);
    setSliderStep(question.step || 1);

    // Drag & Drop
    setDragReorderItems(question.dragItems || []);
    setDragMatchItems(question.matchItems || []);
    setDragCategorizeItems(question.categorizeItems || []);
    setCategories(question.categories || []);

    // Matrix
    setMatrixRows(question.matrixRows || []);
    setMatrixColumns(question.matrixColumns || []);
    setMatrixCellType(question.matrixCellType || "radio");

    // Likert Scale
    setLikertScaleRows(question.likertRows || []);
  };

  useEffect(() => {
    if (selectedBank) {
      const bank = banks.find(b => b.id === selectedBank);
      if (bank) {
        const section = sections.find(s => s.id === bank.sectionId);
        setSelectedSection(section || null);

        // Filter question types based on section's allowed types
        if (section?.allowedQuestionTypes && section.allowedQuestionTypes.length > 0) {
          const allowedTypes = questionTypes.filter(qt =>
            section.allowedQuestionTypes?.includes(qt.code)
          );
          console.log('Filtered allowed types:', allowedTypes);
          setAvailableQuestionTypes(allowedTypes);
        } else {
          // If no allowed types specified, show all question types
          console.log('No allowed types, showing all:', questionTypes);
          setAvailableQuestionTypes(questionTypes);
        }
      }
    }
  }, [selectedBank, banks, sections, questionTypes]);

  useEffect(() => {
    if (selectedSection) {
      const isPsychometric = selectedSection.sectionType === 'psychometric';
      const isHybrid = selectedSection.sectionType === 'mixed' || selectedSection.sectionType === 'hybrid';
      // Show trait selector for psychometric scales and Likert scales in psychometric context
      const isPsychometricScale = questionType === 'likert_scale' || questionType === 'psychometric_scale' || questionType === 'rating_scale';
      setShowTraitSelector(isPsychometric || (isHybrid && isPsychometricScale));
    }
  }, [selectedSection, questionType]);

  const handleBankChange = (bankId: string) => {
    setSelectedBank(bankId);
    setQuestionType("");
    setSelectedTrait("");
    resetDynamicFields();
  };

  const handleMarksChange = (value: string) => {
    setMarks(parseInt(value) || 1);
  };

  const resetDynamicFields = () => {
    // Selection Inputs
    setOptions([]);
    setCorrectAnswer("");
    setDropdownOptions([]);

    // Text Inputs
    setTextDefaultValue("");
    setTextareaDefaultValue("");
    setRichTextContent("");

    // Number Inputs
    setNumberMin(0);
    setNumberMax(100);
    setNumberDefault(0);
    setDecimalMin(0);
    setDecimalMax(100);
    setDecimalDefault(0);
    setDecimalPrecision(2);
    setCurrencySymbol("$");
    setCurrencyDefault(0);

    // Rating & Scale
    setStarRatingMax(5);
    setStarRatingDefault(0);
    setNumericScaleMin(1);
    setNumericScaleMax(10);
    setNumericScaleDefault(5);
    setLikertScaleItems([]);
    setSliderMin(0);
    setSliderMax(100);
    setSliderDefault(50);
    setSliderStep(1);

    // Drag & Drop
    setDragReorderItems([]);
    setDragMatchItems([]);
    setDragCategorizeItems([]);
    setCategories([]);

    // Matrix
    setMatrixRows([]);
    setMatrixColumns([]);

    // Likert
    setLikertScaleRows([]);
  };

  // Selection Inputs Management
  const addOption = () => {
    if (newOptionText.trim()) {
      setOptions([...options, { id: Date.now().toString(), text: newOptionText.trim(), isCorrect: false }]);
      setNewOptionText("");
    }
  };

  const removeOption = (id: string) => {
    setOptions(options.filter(opt => opt.id !== id));
    if (Array.isArray(correctAnswer)) {
      setCorrectAnswer(correctAnswer.filter(ans => ans !== id));
    } else if (correctAnswer === id) {
      setCorrectAnswer("");
    }
  };

  const updateOptionText = (id: string, text: string) => {
    setOptions(options.map(opt => opt.id === id ? { ...opt, text } : opt));
  };

  const toggleCorrectAnswer = (id: string, isMultiple: boolean) => {
    if (isMultiple) {
      const currentAnswers = Array.isArray(correctAnswer) ? correctAnswer : [];
      if (currentAnswers.includes(id)) {
        setCorrectAnswer(currentAnswers.filter(ans => ans !== id));
      } else {
        setCorrectAnswer([...currentAnswers, id]);
      }
    } else {
      setCorrectAnswer(id);
      setOptions(options.map(opt => ({ ...opt, isCorrect: opt.id === id })));
    }
  };

  const addDropdownOption = () => {
    if (newDropdownOption.trim()) {
      setDropdownOptions([...dropdownOptions, newDropdownOption.trim()]);
      setNewDropdownOption("");
    }
  };

  const removeDropdownOption = (index: number) => {
    setDropdownOptions(dropdownOptions.filter((_, i) => i !== index));
  };

  // Likert Scale Management
  const addLikertItem = () => {
    if (newLikertItem.trim()) {
      setLikertScaleItems([...likertScaleItems, { id: Date.now().toString(), text: newLikertItem.trim() }]);
      setNewLikertItem("");
    }
  };

  const removeLikertItem = (id: string) => {
    setLikertScaleItems(likertScaleItems.filter(item => item.id !== id));
  };

  const updateLikertItem = (id: string, text: string) => {
    setLikertScaleItems(likertScaleItems.map(item => item.id === id ? { ...item, text } : item));
  };

  // Drag & Drop Management
  const addDragReorderItem = () => {
    if (newOptionText.trim()) {
      setDragReorderItems([...dragReorderItems, {
        id: Date.now().toString(),
        text: newOptionText.trim(),
        order: dragReorderItems.length + 1
      }]);
      setNewOptionText("");
    }
  };

  const removeDragReorderItem = (id: string) => {
    setDragReorderItems(dragReorderItems.filter(item => item.id !== id));
  };

  const updateDragReorderItem = (id: string, text: string) => {
    setDragReorderItems(dragReorderItems.map(item => item.id === id ? { ...item, text } : item));
  };

  const addDragMatchItem = () => {
    setDragMatchItems([...dragMatchItems, { id: Date.now().toString(), left: "", right: "" }]);
  };

  const updateDragMatchItem = (id: string, field: 'left' | 'right', value: string) => {
    setDragMatchItems(dragMatchItems.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeDragMatchItem = (id: string) => {
    setDragMatchItems(dragMatchItems.filter(item => item.id !== id));
  };

  const addDragCategorizeItem = () => {
    if (newOptionText.trim()) {
      setDragCategorizeItems([...dragCategorizeItems, {
        id: Date.now().toString(),
        text: newOptionText.trim(),
        category: categories[0] || ""
      }]);
      setNewOptionText("");
    }
  };

  const updateDragCategorizeItem = (id: string, text: string) => {
    setDragCategorizeItems(dragCategorizeItems.map(item => item.id === id ? { ...item, text } : item));
  };

  const updateDragCategorizeCategory = (id: string, category: string) => {
    setDragCategorizeItems(dragCategorizeItems.map(item => item.id === id ? { ...item, category } : item));
  };

  const removeDragCategorizeItem = (id: string) => {
    setDragCategorizeItems(dragCategorizeItems.filter(item => item.id !== id));
  };

  const addCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
      setNewCategory("");
    }
  };

  const removeCategory = (category: string) => {
    setCategories(categories.filter(c => c !== category));
  };

  // Matrix Management
  const addMatrixRow = () => {
    if (newRowText.trim()) {
      setMatrixRows([...matrixRows, { id: Date.now().toString(), text: newRowText.trim() }]);
      setNewRowText("");
    }
  };

  const removeMatrixRow = (id: string) => {
    setMatrixRows(matrixRows.filter(row => row.id !== id));
  };

  const updateMatrixRow = (id: string, text: string) => {
    setMatrixRows(matrixRows.map(row => row.id === id ? { ...row, text } : row));
  };

  const addMatrixColumn = () => {
    if (newColumnText.trim()) {
      setMatrixColumns([...matrixColumns, { id: Date.now().toString(), text: newColumnText.trim() }]);
      setNewColumnText("");
    }
  };

  const removeMatrixColumn = (id: string) => {
    setMatrixColumns(matrixColumns.filter(col => col.id !== id));
  };

  const updateMatrixColumn = (id: string, text: string) => {
    setMatrixColumns(matrixColumns.map(col => col.id === id ? { ...col, text } : col));
  };

  // Likert Scale Row Management
  const addLikertRow = () => {
    if (newLikertRow.trim()) {
      setLikertScaleRows([...likertScaleRows, { id: Date.now().toString(), text: newLikertRow.trim() }]);
      setNewLikertRow("");
    }
  };

  const removeLikertRow = (id: string) => {
    setLikertScaleRows(likertScaleRows.filter(row => row.id !== id));
  };

  const updateLikertRow = (id: string, text: string) => {
    setLikertScaleRows(likertScaleRows.map(row => row.id === id ? { ...row, text } : row));
  };

  const handleSubmit = async () => {
    if (!selectedBank) {
      toast.error("Please select a question bank");
      return;
    }
    if (!questionText.trim()) {
      toast.error("Question text is required");
      return;
    }
    if (!questionType) {
      toast.error("Please select a question type");
      return;
    }

    const bank = banks.find(b => b.id === selectedBank);
    const section = sections.find(s => s.id === bank?.sectionId);
    const isPsychometricOrHybridScale = (section?.sectionType === 'psychometric') ||
      ((section?.sectionType === 'mixed' || section?.sectionType === 'hybrid') &&
        (questionType === 'likert_scale' || questionType === 'psychometric_scale' || questionType === 'rating_scale'));

    if (isPsychometricOrHybridScale && !selectedTrait) {
      toast.error("Please select a trait for this psychometric question");
      return;
    }

    // Validate based on question type
    if (!validateQuestionType()) {
      return;
    }

    setLoading(true);
    try {
      const questionData: any = {
        bankId: selectedBank,
        text: questionText.trim(),
        type: questionType,
        traitId: isPsychometricOrHybridScale ? selectedTrait : undefined,
        marks: marks,
        status: "active"
      };

      // Add type-specific data
      addTypeSpecificData(questionData);

      await onSave(questionData);
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error saving question:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateQuestionType = (): boolean => {
    switch (questionType) {
      case 'single_select':
      case 'radio':
        if (options.length < 2) {
          toast.error("Please add at least 2 options");
          return false;
        }
        if (!correctAnswer) {
          toast.error("Please select the correct answer");
          return false;
        }
        break;

      case 'multi_select':
      case 'checkbox':
        if (options.length < 2) {
          toast.error("Please add at least 2 options");
          return false;
        }
        if (!correctAnswer || (Array.isArray(correctAnswer) && correctAnswer.length === 0)) {
          toast.error("Please select at least one correct answer");
          return false;
        }
        break;

      case 'dropdown':
        if (dropdownOptions.length < 2) {
          toast.error("Please add at least 2 dropdown options");
          return false;
        }
        break;

      case 'drag_drop_reorder':
        if (dragReorderItems.length < 2) {
          toast.error("Please add at least 2 items to reorder");
          return false;
        }
        break;

      case 'drag_drop_match':
        if (dragMatchItems.length < 1) {
          toast.error("Please add at least one matching pair");
          return false;
        }
        for (const item of dragMatchItems) {
          if (!item.left.trim() || !item.right.trim()) {
            toast.error("Please complete all matching pairs");
            return false;
          }
        }
        break;

      case 'drag_drop_categorize':
        if (dragCategorizeItems.length < 2) {
          toast.error("Please add at least 2 items to categorize");
          return false;
        }
        if (categories.length < 2) {
          toast.error("Please add at least 2 categories");
          return false;
        }
        break;

      case 'matrix':
        if (matrixRows.length < 1) {
          toast.error("Please add at least one row");
          return false;
        }
        if (matrixColumns.length < 2) {
          toast.error("Please add at least 2 columns");
          return false;
        }
        break;

      case 'likert_scale':
        if (likertScaleRows.length < 1) {
          toast.error("Please add at least one Likert item");
          return false;
        }
        break;
    }
    return true;
  };

  const addTypeSpecificData = (questionData: any) => {
    switch (questionType) {
      case 'single_select':
      case 'radio':
        questionData.options = options;
        questionData.correctAnswer = correctAnswer;
        questionData.uiType = 'radio';
        break;

      case 'multi_select':
      case 'checkbox':
        questionData.options = options;
        questionData.correctAnswer = correctAnswer;
        questionData.uiType = 'checkbox';
        break;

      case 'dropdown':
        questionData.options = dropdownOptions;
        questionData.uiType = 'dropdown';
        break;

      case 'multiselect_dropdown':
        questionData.options = dropdownOptions;
        questionData.uiType = 'multiselect_dropdown';
        break;

      case 'toggle':
        questionData.correctAnswer = trueFalseAnswer;
        questionData.uiType = 'toggle';
        break;

      case 'input_text':
        questionData.defaultValue = textDefaultValue;
        questionData.uiType = 'input_text';
        break;

      case 'textarea':
        questionData.defaultValue = textareaDefaultValue;
        questionData.uiType = 'textarea';
        break;

      case 'rich_text':
        questionData.defaultValue = richTextContent;
        questionData.uiType = 'rich_text';
        break;

      case 'number':
        questionData.min = numberMin;
        questionData.max = numberMax;
        questionData.defaultValue = numberDefault;
        questionData.uiType = 'number';
        break;

      case 'decimal':
        questionData.min = decimalMin;
        questionData.max = decimalMax;
        questionData.defaultValue = decimalDefault;
        questionData.precision = decimalPrecision;
        questionData.uiType = 'decimal';
        break;

      case 'currency':
        questionData.currencySymbol = currencySymbol;
        questionData.defaultValue = currencyDefault;
        questionData.uiType = 'currency';
        break;

      case 'date_picker':
        questionData.dateFormat = dateFormat;
        questionData.uiType = 'date_picker';
        break;

      case 'time_picker':
        questionData.timeFormat = timeFormat;
        questionData.uiType = 'time_picker';
        break;

      case 'datetime_picker':
        questionData.datetimeFormat = datetimeFormat;
        questionData.uiType = 'datetime_picker';
        break;

      case 'rating_star':
        questionData.max = starRatingMax;
        questionData.defaultValue = starRatingDefault;
        questionData.uiType = 'rating_star';
        break;

      case 'rating_scale':
        questionData.min = numericScaleMin;
        questionData.max = numericScaleMax;
        questionData.defaultValue = numericScaleDefault;
        questionData.uiType = 'rating_scale';
        break;

      case 'likert_scale':
        questionData.likertItems = likertScaleItems;
        questionData.likertOptions = likertScaleOptions;
        questionData.uiType = 'likert_scale';
        break;

      case 'slider':
        questionData.min = sliderMin;
        questionData.max = sliderMax;
        questionData.defaultValue = sliderDefault;
        questionData.step = sliderStep;
        questionData.uiType = 'slider';
        break;

      case 'file_upload':
        questionData.allowedTypes = fileAllowedTypes.split(',').map(t => t.trim());
        questionData.maxSize = fileMaxSize;
        questionData.uiType = 'file_upload';
        break;

      case 'image_upload':
        questionData.maxSize = imageMaxSize;
        questionData.dimensions = imageDimensions;
        questionData.uiType = 'image_upload';
        break;

      case 'drag_drop_upload':
        questionData.allowedTypes = fileAllowedTypes.split(',').map(t => t.trim());
        questionData.maxSize = fileMaxSize;
        questionData.uiType = 'drag_drop_upload';
        break;

      case 'signature_pad':
        questionData.uiType = 'signature_pad';
        break;

      case 'location_picker':
        questionData.uiType = 'location_picker';
        break;

      case 'color_picker':
        questionData.defaultValue = colorDefault;
        questionData.uiType = 'color_picker';
        break;

      case 'email':
        questionData.defaultValue = emailDefault;
        questionData.uiType = 'email';
        break;

      case 'phone':
        questionData.defaultValue = phoneDefault;
        questionData.uiType = 'phone';
        break;

      case 'url':
        questionData.defaultValue = urlDefault;
        questionData.uiType = 'url';
        break;

      case 'password':
        questionData.minLength = passwordMinLength;
        questionData.uiType = 'password';
        break;

      case 'drag_reorder':
        questionData.dragItems = dragReorderItems;
        questionData.uiType = 'drag_reorder';
        break;

      case 'drag_match':
        questionData.matchItems = dragMatchItems;
        questionData.uiType = 'drag_match';
        break;

      case 'drag_categorize':
        questionData.categorizeItems = dragCategorizeItems;
        questionData.categories = categories;
        questionData.uiType = 'drag_categorize';
        break;

      case 'matrix':
        questionData.matrixRows = matrixRows;
        questionData.matrixColumns = matrixColumns;
        questionData.matrixCellType = matrixCellType;
        questionData.uiType = 'matrix';
        break;

      case 'section':
        questionData.uiType = 'section';
        break;

      case 'label':
        questionData.defaultValue = textDefaultValue;
        questionData.uiType = 'label';
        break;
    }
  };

  const resetForm = () => {
    setSelectedBank("");
    setQuestionText("");
    setQuestionType("");
    setSelectedTrait("");
    setMarks(1);
    setSelectedSection(null);
    resetDynamicFields();
  };

  const renderDynamicFields = () => {
    // Map answer types to UI types and render appropriate fields

    // Selection Inputs
    if (questionType === 'single_select' || questionType === 'radio') {
      return renderRadioFields();
    }

    if (questionType === 'multi_select' || questionType === 'checkbox') {
      return renderCheckboxFields();
    }

    if (questionType === 'dropdown') {
      return renderDropdownFields();
    }

    if (questionType === 'multiselect_dropdown') {
      return renderMultiSelectDropdownFields();
    }

    if (questionType === 'toggle') {
      return renderToggleFields();
    }

    // Text Inputs
    if (questionType === 'input_text') {
      return renderTextInputFields();
    }

    if (questionType === 'textarea') {
      return renderTextareaFields();
    }

    if (questionType === 'rich_text') {
      return renderRichTextFields();
    }

    // Number Inputs
    if (questionType === 'number') {
      return renderNumberFields();
    }

    if (questionType === 'decimal') {
      return renderDecimalFields();
    }

    if (questionType === 'currency') {
      return renderCurrencyFields();
    }

    // Date & Time
    if (questionType === 'date_picker') {
      return renderDateFields();
    }

    if (questionType === 'time_picker') {
      return renderTimeFields();
    }

    if (questionType === 'datetime_picker') {
      return renderDatetimeFields();
    }

    // Rating & Scale
    if (questionType === 'rating_star') {
      return renderStarRatingFields();
    }

    if (questionType === 'rating_scale') {
      return renderNumericScaleFields();
    }

    if (questionType === 'likert_scale') {
      return renderLikertScaleFields();
    }

    if (questionType === 'slider') {
      return renderSliderFields();
    }

    // File & Media
    if (questionType === 'file_upload') {
      return renderFileUploadFields();
    }

    if (questionType === 'image_upload') {
      return renderImageUploadFields();
    }

    if (questionType === 'drag_drop_upload') {
      return renderDragDropUploadFields();
    }

    // Advanced Inputs
    if (questionType === 'signature_pad') {
      return renderSignatureFields();
    }

    if (questionType === 'location_picker') {
      return renderLocationFields();
    }

    if (questionType === 'color_picker') {
      return renderColorPickerFields();
    }

    // Structured Inputs
    if (questionType === 'email') {
      return renderEmailFields();
    }

    if (questionType === 'phone') {
      return renderPhoneFields();
    }

    if (questionType === 'url') {
      return renderUrlFields();
    }

    if (questionType === 'password') {
      return renderPasswordFields();
    }

    // Drag & Drop UI
    if (questionType === 'drag_reorder') {
      return renderDragReorderFields();
    }

    if (questionType === 'drag_match') {
      return renderDragMatchFields();
    }

    if (questionType === 'drag_categorize') {
      return renderDragCategorizeFields();
    }

    // Special Cases
    if (questionType === 'matrix') {
      return renderMatrixFields();
    }

    if (questionType === 'section') {
      return renderSectionFields();
    }

    if (questionType === 'label') {
      return renderLabelFields();
    }

    return null;
  };

  // Render methods for each question type
  const renderRadioFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Radio Button Options (Single Select)</p>
        {renderOptionsList(true)}
      </div>
    </div>
  );

  const renderCheckboxFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Checkbox Options (Multi Select)</p>
        {renderOptionsList(false)}
      </div>
    </div>
  );

  const renderDropdownFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Dropdown Options</p>
        {renderDropdownOptionsList()}
      </div>
    </div>
  );

  const renderMultiSelectDropdownFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Multi-Select Dropdown Options</p>
        {renderDropdownOptionsList()}
      </div>
    </div>
  );

  const renderToggleFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <p className="text-sm font-medium text-blue-800 mb-2">Default Value</p>
      <div className="flex gap-4">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={trueFalseAnswer === true}
            onChange={() => setTrueFalseAnswer(true)}
            className="w-4 h-4 text-indigo-600"
          />
          <span>Yes / True</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={trueFalseAnswer === false}
            onChange={() => setTrueFalseAnswer(false)}
            className="w-4 h-4 text-indigo-600"
          />
          <span>No / False</span>
        </label>
      </div>
    </div>
  );

  const renderTextInputFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-2">Default Value (Optional)</label>
      <input
        type="text"
        value={textDefaultValue}
        onChange={(e) => setTextDefaultValue(e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="Enter default text..."
      />
    </div>
  );

  const renderTextareaFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-2">Default Value (Optional)</label>
      <textarea
        value={textareaDefaultValue}
        onChange={(e) => setTextareaDefaultValue(e.target.value)}
        rows={3}
        className="w-full p-2 border rounded"
        placeholder="Enter default text..."
      />
    </div>
  );

  const renderRichTextFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-2">Default Content (Optional)</label>
      <textarea
        value={richTextContent}
        onChange={(e) => setRichTextContent(e.target.value)}
        rows={4}
        className="w-full p-2 border rounded font-mono text-sm"
        placeholder="Enter rich text content with HTML tags..."
      />
      <p className="text-xs text-gray-500 mt-1">Supports HTML formatting</p>
    </div>
  );

  const renderNumberFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Minimum Value</label>
        <input
          type="number"
          value={numberMin}
          onChange={(e) => setNumberMin(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Maximum Value</label>
        <input
          type="number"
          value={numberMax}
          onChange={(e) => setNumberMax(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Value</label>
        <input
          type="number"
          value={numberDefault}
          onChange={(e) => setNumberDefault(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderDecimalFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Minimum Value</label>
        <input
          type="number"
          step="0.01"
          value={decimalMin}
          onChange={(e) => setDecimalMin(parseFloat(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Maximum Value</label>
        <input
          type="number"
          step="0.01"
          value={decimalMax}
          onChange={(e) => setDecimalMax(parseFloat(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Decimal Precision</label>
        <input
          type="number"
          min="0"
          max="6"
          value={decimalPrecision}
          onChange={(e) => setDecimalPrecision(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Value</label>
        <input
          type="number"
          step="0.01"
          value={decimalDefault}
          onChange={(e) => setDecimalDefault(parseFloat(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderCurrencyFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Currency Symbol</label>
        <input
          type="text"
          value={currencySymbol}
          onChange={(e) => setCurrencySymbol(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="$"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Value</label>
        <input
          type="number"
          step="0.01"
          value={currencyDefault}
          onChange={(e) => setCurrencyDefault(parseFloat(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderDateFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Date Format</label>
      <select
        value={dateFormat}
        onChange={(e) => setDateFormat(e.target.value)}
        className="w-full p-2 border rounded"
      >
        <option value="YYYY-MM-DD">YYYY-MM-DD (2024-01-15)</option>
        <option value="MM/DD/YYYY">MM/DD/YYYY (01/15/2024)</option>
        <option value="DD/MM/YYYY">DD/MM/YYYY (15/01/2024)</option>
        <option value="MMM DD, YYYY">MMM DD, YYYY (Jan 15, 2024)</option>
      </select>
    </div>
  );

  const renderTimeFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Time Format</label>
      <select
        value={timeFormat}
        onChange={(e) => setTimeFormat(e.target.value)}
        className="w-full p-2 border rounded"
      >
        <option value="HH:mm">24-hour (14:30)</option>
        <option value="hh:mm A">12-hour (02:30 PM)</option>
      </select>
    </div>
  );

  const renderDatetimeFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Date & Time Format</label>
      <select
        value={datetimeFormat}
        onChange={(e) => setDatetimeFormat(e.target.value)}
        className="w-full p-2 border rounded"
      >
        <option value="YYYY-MM-DD HH:mm">YYYY-MM-DD HH:mm (2024-01-15 14:30)</option>
        <option value="MM/DD/YYYY hh:mm A">MM/DD/YYYY hh:mm A (01/15/2024 02:30 PM)</option>
        <option value="DD/MM/YYYY HH:mm">DD/MM/YYYY HH:mm (15/01/2024 14:30)</option>
      </select>
    </div>
  );

  const renderStarRatingFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Maximum Rating (Stars)</label>
        <input
          type="number"
          min="1"
          max="10"
          value={starRatingMax}
          onChange={(e) => setStarRatingMax(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Rating</label>
        <input
          type="number"
          min="0"
          max={starRatingMax}
          value={starRatingDefault}
          onChange={(e) => setStarRatingDefault(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderNumericScaleFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Scale Start</label>
        <input
          type="number"
          value={numericScaleMin}
          onChange={(e) => setNumericScaleMin(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Scale End</label>
        <input
          type="number"
          value={numericScaleMax}
          onChange={(e) => setNumericScaleMax(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Value</label>
        <input
          type="number"
          min={numericScaleMin}
          max={numericScaleMax}
          value={numericScaleDefault}
          onChange={(e) => setNumericScaleDefault(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderLikertScaleFields = () => (
    <div className="space-y-4">
      <div className="bg-purple-50 rounded-lg p-3">
        <p className="text-sm font-medium text-purple-800 mb-2">Likert Scale Items (Rows)</p>
        {likertScaleRows.map((item) => (
          <div key={item.id} className="flex gap-2 mb-2">
            <input
              type="text"
              value={item.text}
              onChange={(e) => updateLikertRow(item.id, e.target.value)}
              className="flex-1 p-2 border rounded text-sm"
              placeholder="Item text (e.g., I feel satisfied with my work)"
            />
            <button
              onClick={() => removeLikertRow(item.id)}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newLikertRow}
            onChange={(e) => setNewLikertRow(e.target.value)}
            placeholder="New Likert item..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addLikertRow}>
            Add Item
          </Button>
        </div>
      </div>

      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-sm font-medium text-green-800 mb-2">Likert Scale Options (Columns)</p>
        <div className="space-y-2">
          {likertScaleOptions.map((option, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                type="text"
                value={option}
                onChange={(e) => {
                  const newOptions = [...likertScaleOptions];
                  newOptions[idx] = e.target.value;
                  setLikertScaleOptions(newOptions);
                }}
                className="flex-1 p-2 border rounded text-sm"
              />
              <button
                onClick={() => {
                  const newOptions = likertScaleOptions.filter((_, i) => i !== idx);
                  setLikertScaleOptions(newOptions);
                }}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setLikertScaleOptions([...likertScaleOptions, "New Option"])}
          >
            + Add Option
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSliderFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Minimum Value</label>
        <input
          type="number"
          value={sliderMin}
          onChange={(e) => setSliderMin(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Maximum Value</label>
        <input
          type="number"
          value={sliderMax}
          onChange={(e) => setSliderMax(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Step Size</label>
        <input
          type="number"
          min="1"
          value={sliderStep}
          onChange={(e) => setSliderStep(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Default Value</label>
        <input
          type="number"
          min={sliderMin}
          max={sliderMax}
          value={sliderDefault}
          onChange={(e) => setSliderDefault(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderFileUploadFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Allowed File Types</label>
        <input
          type="text"
          value={fileAllowedTypes}
          onChange={(e) => setFileAllowedTypes(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="e.g., .pdf,.doc,.docx (comma-separated)"
        />
        <p className="text-xs text-gray-500 mt-1">Separate with commas: .pdf,.jpg,.png</p>
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Max File Size (MB)</label>
        <input
          type="number"
          min="1"
          value={fileMaxSize}
          onChange={(e) => setFileMaxSize(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderImageUploadFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Max File Size (MB)</label>
        <input
          type="number"
          min="1"
          value={imageMaxSize}
          onChange={(e) => setImageMaxSize(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Dimensions (Optional)</label>
        <input
          type="text"
          value={imageDimensions}
          onChange={(e) => setImageDimensions(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="e.g., 800x600"
        />
      </div>
    </div>
  );

  const renderDragDropUploadFields = () => (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3">
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Allowed File Types</label>
        <input
          type="text"
          value={fileAllowedTypes}
          onChange={(e) => setFileAllowedTypes(e.target.value)}
          className="w-full p-2 border rounded"
          placeholder="e.g., .pdf,.jpg,.png (comma-separated)"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-blue-800 block mb-1">Max File Size (MB)</label>
        <input
          type="number"
          min="1"
          value={fileMaxSize}
          onChange={(e) => setFileMaxSize(parseInt(e.target.value))}
          className="w-full p-2 border rounded"
        />
      </div>
    </div>
  );

  const renderSignatureFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <p className="text-sm text-gray-600">Users will be able to draw their signature using mouse or touch.</p>
    </div>
  );

  const renderLocationFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <p className="text-sm text-gray-600">Users can select their location using GPS or map picker.</p>
    </div>
  );

  const renderColorPickerFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Default Color</label>
      <input
        type="color"
        value={colorDefault}
        onChange={(e) => setColorDefault(e.target.value)}
        className="w-full p-2 border rounded h-12"
      />
    </div>
  );

  const renderEmailFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Default Email (Optional)</label>
      <input
        type="email"
        value={emailDefault}
        onChange={(e) => setEmailDefault(e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="example@domain.com"
      />
    </div>
  );

  const renderPhoneFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Default Phone Number (Optional)</label>
      <input
        type="tel"
        value={phoneDefault}
        onChange={(e) => setPhoneDefault(e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="+1 234 567 8900"
      />
    </div>
  );

  const renderUrlFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Default URL (Optional)</label>
      <input
        type="url"
        value={urlDefault}
        onChange={(e) => setUrlDefault(e.target.value)}
        className="w-full p-2 border rounded"
        placeholder="https://example.com"
      />
    </div>
  );

  const renderPasswordFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-1">Minimum Length</label>
      <input
        type="number"
        min="1"
        value={passwordMinLength}
        onChange={(e) => setPasswordMinLength(parseInt(e.target.value))}
        className="w-full p-2 border rounded"
      />
    </div>
  );

  const renderDragReorderFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Items to Reorder</p>
        {dragReorderItems.map((item) => (
          <div key={item.id} className="flex gap-2 mb-2">
            <span className="text-gray-500 self-center">⋮⋮</span>
            <input
              type="text"
              value={item.text}
              onChange={(e) => updateDragReorderItem(item.id, e.target.value)}
              className="flex-1 p-2 border rounded text-sm"
              placeholder="Item text"
            />
            <button
              onClick={() => removeDragReorderItem(item.id)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newOptionText}
            onChange={(e) => setNewOptionText(e.target.value)}
            placeholder="New item..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addDragReorderItem}>
            Add Item
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDragMatchFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Matching Pairs</p>
        {dragMatchItems.map((item) => (
          <div key={item.id} className="flex gap-2 mb-2">
            <input
              type="text"
              value={item.left}
              onChange={(e) => updateDragMatchItem(item.id, 'left', e.target.value)}
              placeholder="Left item"
              className="flex-1 p-2 border rounded text-sm"
            />
            <span className="self-center">↔</span>
            <input
              type="text"
              value={item.right}
              onChange={(e) => updateDragMatchItem(item.id, 'right', e.target.value)}
              placeholder="Right item"
              className="flex-1 p-2 border rounded text-sm"
            />
            <button
              onClick={() => removeDragMatchItem(item.id)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
        <Button variant="secondary" size="sm" onClick={addDragMatchItem}>
          + Add Pair
        </Button>
      </div>
    </div>
  );

  const renderDragCategorizeFields = () => (
    <div className="space-y-4">
      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-sm font-medium text-green-800 mb-2">Categories</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {categories.map((category) => (
            <span key={category} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm flex items-center gap-2">
              {category}
              <button onClick={() => removeCategory(category)} className="text-red-500 hover:text-red-700">×</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addCategory}>
            Add Category
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Items to Categorize</p>
        {dragCategorizeItems.map((item) => (
          <div key={item.id} className="flex gap-2 mb-2">
            <input
              type="text"
              value={item.text}
              onChange={(e) => updateDragCategorizeItem(item.id, e.target.value)}
              className="flex-1 p-2 border rounded text-sm"
              placeholder="Item text"
            />
            <select
              value={item.category}
              onChange={(e) => updateDragCategorizeCategory(item.id, e.target.value)}
              className="w-32 p-2 border rounded text-sm"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button
              onClick={() => removeDragCategorizeItem(item.id)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newOptionText}
            onChange={(e) => setNewOptionText(e.target.value)}
            placeholder="New item..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addDragCategorizeItem}>
            Add Item
          </Button>
        </div>
      </div>
    </div>
  );

  const renderMatrixFields = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm font-medium text-blue-800 mb-2">Matrix Rows</p>
        {matrixRows.map((row) => (
          <div key={row.id} className="flex gap-2 mb-2">
            <input
              type="text"
              value={row.text}
              onChange={(e) => updateMatrixRow(row.id, e.target.value)}
              className="flex-1 p-2 border rounded text-sm"
              placeholder="Row label"
            />
            <button
              onClick={() => removeMatrixRow(row.id)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newRowText}
            onChange={(e) => setNewRowText(e.target.value)}
            placeholder="New row..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addMatrixRow}>
            Add Row
          </Button>
        </div>
      </div>

      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-sm font-medium text-green-800 mb-2">Matrix Columns</p>
        {matrixColumns.map((col) => (
          <div key={col.id} className="flex gap-2 mb-2">
            <input
              type="text"
              value={col.text}
              onChange={(e) => updateMatrixColumn(col.id, e.target.value)}
              className="flex-1 p-2 border rounded text-sm"
              placeholder="Column label"
            />
            <button
              onClick={() => removeMatrixColumn(col.id)}
              className="text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <input
            type="text"
            value={newColumnText}
            onChange={(e) => setNewColumnText(e.target.value)}
            placeholder="New column..."
            className="flex-1 p-2 border rounded text-sm"
          />
          <Button variant="secondary" size="sm" onClick={addMatrixColumn}>
            Add Column
          </Button>
        </div>
      </div>

      <div className="bg-yellow-50 rounded-lg p-3">
        <label className="text-sm font-medium text-yellow-800 block mb-2">Cell Type</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={matrixCellType === "radio"}
              onChange={() => setMatrixCellType("radio")}
              className="w-4 h-4 text-indigo-600"
            />
            <span>Radio (Single Select per row)</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={matrixCellType === "checkbox"}
              onChange={() => setMatrixCellType("checkbox")}
              className="w-4 h-4 text-indigo-600"
            />
            <span>Checkbox (Multi Select per row)</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSectionFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-2">Section Description</label>
      <textarea
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        rows={4}
        className="w-full p-2 border rounded"
        placeholder="This section will be displayed as a group heading..."
      />
      <p className="text-xs text-gray-500 mt-1">This question type acts as a section divider/heading only</p>
    </div>
  );

  const renderLabelFields = () => (
    <div className="bg-blue-50 rounded-lg p-3">
      <label className="text-sm font-medium text-blue-800 block mb-2">Label Text</label>
      <textarea
        value={questionText}
        onChange={(e) => setQuestionText(e.target.value)}
        rows={3}
        className="w-full p-2 border rounded"
        placeholder="Enter static text to display..."
      />
      <p className="text-xs text-gray-500 mt-1">This displays static text/label without any input</p>
    </div>
  );

  const renderOptionsList = (isSingleSelect: boolean) => (
    <>
      {options.map((opt) => (
        <div key={opt.id} className="flex items-center gap-2 mb-2">
          <input
            type={isSingleSelect ? "radio" : "checkbox"}
            checked={isSingleSelect ? correctAnswer === opt.id : (Array.isArray(correctAnswer) && correctAnswer.includes(opt.id))}
            onChange={() => toggleCorrectAnswer(opt.id, !isSingleSelect)}
            className="w-4 h-4 text-indigo-600"
          />
          <input
            type="text"
            value={opt.text}
            onChange={(e) => updateOptionText(opt.id, e.target.value)}
            className="flex-1 p-2 border rounded text-sm"
            placeholder="Option text"
          />
          <button
            onClick={() => removeOption(opt.id)}
            className="text-red-500 hover:text-red-700 text-sm"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="flex gap-2 mt-3">
        <input
          type="text"
          value={newOptionText}
          onChange={(e) => setNewOptionText(e.target.value)}
          placeholder="New option..."
          className="flex-1 p-2 border rounded text-sm"
        />
        <Button variant="secondary" size="sm" onClick={addOption}>
          Add Option
        </Button>
      </div>
    </>
  );

  const renderDropdownOptionsList = () => (
    <>
      {dropdownOptions.map((option, idx) => (
        <div key={idx} className="flex gap-2 mb-2">
          <input
            type="text"
            value={option}
            onChange={(e) => {
              const newOptions = [...dropdownOptions];
              newOptions[idx] = e.target.value;
              setDropdownOptions(newOptions);
            }}
            className="flex-1 p-2 border rounded text-sm"
            placeholder="Option text"
          />
          <button
            onClick={() => removeDropdownOption(idx)}
            className="text-red-500 hover:text-red-700 text-sm"
          >
            Remove
          </button>
        </div>
      ))}
      <div className="flex gap-2 mt-3">
        <input
          type="text"
          value={newDropdownOption}
          onChange={(e) => setNewDropdownOption(e.target.value)}
          placeholder="New option..."
          className="flex-1 p-2 border rounded text-sm"
        />
        <Button variant="secondary" size="sm" onClick={addDropdownOption}>
          Add Option
        </Button>
      </div>
    </>
  );

  const bankOptions = banks.map(b => ({
    value: b.id,
    label: `${b.name} (${b.sectionName || "No Section"})`
  }));

  const traitOptions = traits.map(t => ({
    value: t.id,
    label: `${t.name} (${t.scaleType || "No scale"})`
  }));

  // Mapping of question type codes to display labels (as fallback if qt.name is not available)
  const QUESTION_TYPE_LABELS: Record<string, string> = {
    single_select: "Single Select (Radio / Scale)",
    multi_select: "Multi Select (Checkbox)",
    dropdown: "Dropdown Select",
    multiselect_dropdown: "Multi Select Dropdown",
    toggle: "Toggle Switch (Yes/No)",
    input_text: "Text Input (Single Line)",
    textarea: "Textarea (Paragraph)",
    rich_text: "Rich Text Editor",
    number: "Number Input",
    decimal: "Decimal Input",
    currency: "Currency Input",
    date_picker: "Date Picker",
    time_picker: "Time Picker",
    datetime_picker: "Date & Time Picker",
    rating_star: "Star Rating",
    rating_scale: "Numeric Scale (1–10)",
    likert_scale: "Likert Scale (Agree/Disagree)",
    slider: "Slider",
    file_upload: "File Upload",
    image_upload: "Image Upload",
    drag_drop_upload: "Drag & Drop Upload",
    signature_pad: "Signature Pad",
    location_picker: "Location Picker (GPS)",
    color_picker: "Color Picker",
    email: "Email Input",
    phone: "Phone Input",
    url: "URL Input",
    password: "Password Input",
    drag_reorder: "Drag & Drop Reordering",
    drag_match: "Drag & Drop Matching",
    drag_categorize: "Drag & Drop Categorization",
    matrix: "Matrix / Table Input",
    section: "Section / Group (UI Only)",
    label: "Static Text / Label"
  };

  const questionTypeOptions = availableQuestionTypes.map(qt => ({
    value: qt.answerFormat, // Use answerFormat as the value to match with questionType state
    label: qt.name || QUESTION_TYPE_LABELS[qt.answerFormat] || qt.answerFormat
  }));

  // Add this console log for debugging (remove in production)
  console.log('availableQuestionTypes:', availableQuestionTypes);
  console.log('questionTypes prop:', questionTypes);
  console.log('selectedSection?.allowedQuestionTypes:', selectedSection?.allowedQuestionTypes);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { onClose(); resetForm(); }}
      title={editingQuestion ? "Edit Question" : "Create New Question"}
      size="lg"
    >
      <div className="space-y-5 max-h-[70vh] overflow-y-auto px-1">
        <div>
          <Select
            label="Question Bank"
            value={selectedBank}
            onChange={handleBankChange}
            options={bankOptions}
            required
            disabled={!!editingQuestion}
          />
        </div>

        {selectedSection && (
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-3 border border-blue-100">
            <p className="text-xs text-gray-500">Section: <span className="font-medium text-gray-700">{selectedSection.name}</span></p>
            <p className="text-xs text-gray-500 mt-1">Type: <span className="font-medium capitalize">{selectedSection.sectionType === 'mixed' ? 'Hybrid' : selectedSection.sectionType}</span></p>
            {selectedSection.allowedQuestionTypes && (
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs text-gray-500">Allowed Types:</span>
                {selectedSection.allowedQuestionTypes.map(typeCode => {
                  const qt = questionTypes.find(t => t.code === typeCode);
                  return qt ? (
                    <span key={typeCode} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      {qt.name}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>
        )}

        <div>
          <Textarea
            label="Question Text"
            value={questionText}
            onChange={setQuestionText}
            placeholder="Enter your question here..."
            rows={3}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Select
              label="Question Type"
              value={questionType}
              onChange={setQuestionType}
              options={questionTypeOptions}
              required
              disabled={availableQuestionTypes.length === 0}
            />
            {availableQuestionTypes.length === 0 && selectedSection && (
              <p className="text-xs text-amber-600 mt-1">
                No question types allowed for this section. Please configure allowed types in section settings.
              </p>
            )}
          </div>
          <div>
            <Input
              label="Marks"
              type="number"
              value={String(marks)}
              onChange={handleMarksChange}
              min="1"
              required
            />
          </div>
        </div>

        {showTraitSelector && (
          <div>
            <Select
              label="Linked Trait (Psychometric)"
              value={selectedTrait}
              onChange={setSelectedTrait}
              options={traitOptions}
              required
            />
            <p className="text-xs text-purple-600 mt-1">
              This question will measure the selected psychological trait using a scale format.
            </p>
          </div>
        )}

        {questionType && renderDynamicFields()}

        <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200">
          <p className="text-xs text-yellow-700">
            <strong>ℹ️ Note:</strong> Based on the selected question type, you'll need to provide the appropriate answer format and configuration.
          </p>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={() => { onClose(); resetForm(); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-gradient-to-r from-indigo-600 to-purple-600"
            disabled={loading || !selectedBank || !questionText.trim() || !questionType || (showTraitSelector && !selectedTrait)}
          >
            {loading ? "Saving..." : (editingQuestion ? "Update" : "Create")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuestionModal;